/* ==========================================================================
   BALZY core behaviours — scroll reveal, carousels, FAQ accordion,
   cycling word. One delegated controller for every balzy-* section.
   Re-initialises on Shopify theme-editor section load.
   ========================================================================== */
(function () {
  'use strict';

  // Every section links this file, so the browser may execute it more than
  // once. Run the real setup only the first time.
  if (window.BalzyCore) return;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var observer = null;

  /* ---- scroll reveal --------------------------------------------------- */
  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.style.opacity = '1';
        entry.target.style.transform = 'none';
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -8% 0px' });
    return observer;
  }

  function initReveal(root) {
    if (REDUCED || !('IntersectionObserver' in window)) return;
    var io = getObserver();
    if (!io) return;
    root.querySelectorAll('[data-balzy-reveal]').forEach(function (el) {
      if (el.dataset.balzyRevealReady) return;
      el.dataset.balzyRevealReady = '1';
      el.style.opacity = '0';
      el.style.transform = 'translateY(32px)';
      el.style.transition = 'opacity 0.7s ease, transform 0.75s cubic-bezier(0.16,0.8,0.3,1)';
      el.style.willChange = 'opacity, transform';
      io.observe(el);
    });
  }

  /* ---- carousels ------------------------------------------------------- */
  function stepFor(track) {
    var mode = track.dataset.balzyStep || 'card';
    if (mode === 'page') return track.clientWidth * 0.8;
    if (!isNaN(parseFloat(mode))) return parseFloat(mode);
    var card = track.firstElementChild;
    if (!card) return 300;
    var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 20);
    return card.getBoundingClientRect().width + (isNaN(gap) ? 20 : gap);
  }

  function initCarousels(root) {
    root.querySelectorAll('[data-balzy-carousel]').forEach(function (wrap) {
      if (wrap.dataset.balzyCarouselReady) return;
      wrap.dataset.balzyCarouselReady = '1';
      var track = wrap.querySelector('[data-balzy-track]');
      if (!track) return;
      wrap.querySelectorAll('[data-balzy-scroll]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var dir = btn.dataset.balzyScroll === 'prev' ? -1 : 1;
          track.scrollBy({ left: dir * stepFor(track), behavior: REDUCED ? 'auto' : 'smooth' });
        });
      });
    });
  }

  /* ---- FAQ accordion --------------------------------------------------- */
  function initFaq(root) {
    root.querySelectorAll('[data-balzy-faq]').forEach(function (list) {
      if (list.dataset.balzyFaqReady) return;
      list.dataset.balzyFaqReady = '1';
      var toggles = list.querySelectorAll('[data-balzy-faq-toggle]');
      toggles.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('[data-balzy-faq-item]');
          var isOpen = item.classList.contains('is-open');
          // accordion: only one open at a time
          list.querySelectorAll('[data-balzy-faq-item].is-open').forEach(function (other) {
            other.classList.remove('is-open');
            other.querySelector('[data-balzy-faq-toggle]').setAttribute('aria-expanded', 'false');
            other.querySelector('[data-balzy-faq-panel]').hidden = true;
          });
          if (!isOpen) {
            item.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
            item.querySelector('[data-balzy-faq-panel]').hidden = false;
          }
        });
      });
    });
  }

  /* ---- cycling word ---------------------------------------------------- */
  var timers = [];
  function initCycle(root) {
    root.querySelectorAll('[data-balzy-cycle]').forEach(function (el) {
      if (el.dataset.balzyCycleReady) return;
      el.dataset.balzyCycleReady = '1';
      var words = (el.dataset.balzyCycle || '').split('|').map(function (w) { return w.trim(); }).filter(Boolean);
      if (words.length < 2) return;
      var interval = parseInt(el.dataset.balzyCycleInterval, 10) || 1900;
      var i = 0;
      var timer = setInterval(function () {
        i = (i + 1) % words.length;
        el.textContent = words[i];
        if (!REDUCED) {
          el.style.animation = 'none';
          void el.offsetWidth; // reflow so the animation restarts
          el.style.animation = 'balzyUp 0.5s ease-out';
        }
      }, interval);
      timers.push({ el: el, timer: timer });
    });
  }

  function clearTimersIn(root) {
    timers = timers.filter(function (t) {
      if (root.contains(t.el)) { clearInterval(t.timer); return false; }
      return true;
    });
  }

  /* ---- boot ------------------------------------------------------------ */
  function initAll(root) {
    root = root || document;
    initReveal(root);
    initCarousels(root);
    initFaq(root);
    initCycle(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Shopify theme editor
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { clearTimersIn(e.target); });

  window.BalzyCore = { init: initAll };
})();
